import { useState, useEffect } from 'react';
import { autoplannerAPI } from '../../services/api';

const getInitialStep = (task) => {
  if (!task) return 0;

  switch (task.status) {
    case 'draft':
      return 2; // Step 3 (Validation) - 0-indexed
    case 'validated':
      return 3; // Step 4 (Locked Seatings) - 0-indexed
    case 'set_algo_preferences':
      return 5; // Step 6 (Confirmation) - 0-indexed
    case 'cancelled':
      return 2; // Treat cancelled similar to draft: resume at Validation
    case 'pending_for_algorithm':
    case 'processing_algorithm':
    case 'failed':
      return 6; // Step 7 (Processing) - 0-indexed
    case 'completed':
      return 7; // Step 8 (Results) - 0-indexed
    default:
      return 0; // Start from beginning for other statuses
  }
};

export const useAutoplannerTaskManager = (selectedTask, setFormData, setValidationResult) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [taskId, setTaskId] = useState(null);

  useEffect(() => {
    if (selectedTask) {
      console.log('Loading selectedTask:', selectedTask);
      const trainingGroupId = selectedTask.training_group_id || selectedTask.training_group?.id;
      let sessionExclusions = {};
      if (selectedTask.session_exclusions) {
        try {
          sessionExclusions = JSON.parse(selectedTask.session_exclusions);
        } catch (e) {
          console.warn('Failed to parse session exclusions:', e);
        }
      }

      let resolutions = {};
      if (selectedTask.resolutions) {
        try {
          resolutions = JSON.parse(selectedTask.resolutions);
        } catch (e) {
          console.warn('Failed to parse resolutions:', e);
        }
      }

      let sessionOrder = {};
      if (selectedTask.session_order) {
        try {
          sessionOrder = JSON.parse(selectedTask.session_order);
        } catch (e) {
          console.warn('Failed to parse session order:', e);
        }
      }

      let lockedSeatings = null;
      if (selectedTask.locked_seatings) {
        try {
          lockedSeatings = JSON.parse(selectedTask.locked_seatings);
          console.log('Loaded existing locked seatings:', lockedSeatings);
        } catch (e) {
          console.warn('Failed to parse locked seatings:', e);
        }
      }

      const selectedSessions = Array.isArray(selectedTask.sessions) ? selectedTask.sessions : [];

      // Prime form immediately so reopening a draft does not leave trainingGroupId / dates
      // null while async session hydration runs (avoids "Missing training group ID" on validation).
      setFormData((prev) => ({
        ...prev,
        trainingGroupId,
        lockedSeatings,
        startDate: selectedTask.start_date,
        endDate: selectedTask.end_date,
        promptType: selectedTask.prompt_type || 'crew_consistent',
        sessionExclusions,
        resolutions,
        sessionOrder,
      }));

      const hydrateAndSetForm = async (sessionIds) => {
        try {
          if (sessionIds && sessionIds.length > 0) {
            const fullSessions = await autoplannerAPI.getSessionsByIds(sessionIds);
            setFormData((prev) => ({
              ...prev,
              selectedSessions: fullSessions,
              trainingGroupId,
              lockedSeatings,
              startDate: selectedTask.start_date,
              endDate: selectedTask.end_date,
              promptType: selectedTask.prompt_type || 'crew_consistent',
              sessionExclusions,
              resolutions,
              sessionOrder,
            }));
          } else {
            setFormData((prev) => ({
              ...prev,
              selectedSessions: [],
              trainingGroupId,
              lockedSeatings,
              startDate: selectedTask.start_date,
              endDate: selectedTask.end_date,
              promptType: selectedTask.prompt_type || 'crew_consistent',
              sessionExclusions,
              resolutions,
              sessionOrder,
            }));
          }
        } catch (error) {
          console.error('Failed to hydrate sessions for task:', error);
          const fallbackSessions = (sessionIds || []).map((id) => ({ id }));
          setFormData((prev) => ({
            ...prev,
            selectedSessions: fallbackSessions,
            trainingGroupId,
            startDate: selectedTask.start_date,
            endDate: selectedTask.end_date,
            promptType: selectedTask.prompt_type || 'crew_consistent',
            sessionExclusions,
            resolutions,
            sessionOrder,
          }));
        }
      };

      if (!selectedSessions || selectedSessions.length === 0) {
        // Always fetch fresh task from backend to ensure sessions are included
        (async () => {
          try {
            const taskData = await autoplannerAPI.getAutoplannerTask(selectedTask.id);
            const sessionIds = Array.isArray(taskData.sessions) ? taskData.sessions : [];
            await hydrateAndSetForm(sessionIds);
          } catch (error) {
            console.error('Error fetching task sessions from backend:', error);
            await hydrateAndSetForm([]);
          }
        })();
      } else {
        // Normalize to IDs if objects were somehow passed
        const first = selectedSessions[0];
        const sessionIds =
          typeof first === 'number' ? selectedSessions : selectedSessions.map((s) => s.id);
        (async () => {
          await hydrateAndSetForm(sessionIds);
        })();
      }

      setTaskId(selectedTask.id);
      const initialStep = getInitialStep(selectedTask);
      setCurrentStep(initialStep);

      if (selectedTask.status === 'processing_algorithm') {
        setProcessing(true);
      }

      if (selectedTask.status === 'draft' && selectedTask.availability_check) {
        try {
          const availabilityCheck = JSON.parse(selectedTask.availability_check);
          setValidationResult(availabilityCheck);
        } catch (e) {
          console.warn('Failed to parse availability check:', e);
        }
      }

      if (selectedTask.scheduling_feasibility) {
        try {
          const schedulingFeasibility = JSON.parse(selectedTask.scheduling_feasibility);
          console.log('Loaded scheduling feasibility:', schedulingFeasibility);
        } catch (e) {
          console.warn('Failed to parse scheduling feasibility:', e);
        }
      }
    }
  }, [selectedTask, setFormData, setValidationResult]);

  return {
    currentStep,
    setCurrentStep,
    processing,
    setProcessing,
    taskId,
    setTaskId,
    getInitialStep,
  };
};
